const en = {
  common: {
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    phone: 'Phone',
    email: 'Email',
    office: 'Office',
    availability: 'Availability',
    support: 'Support',
    preOrder: 'Pre-order',
    clearForm: 'Clear form',
    sending: 'Sending...',
    select: 'Select',
    closeNotification: 'Close notification',
  },
  nav: {
    home: 'Home',
    about: 'About',
    preOrder: 'Pre-Order',
    contacts: 'Contacts',
    startPreOrder: 'Start Pre-Order',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    logoAria: 'KUB AUTO home',
    languageSwitcher: 'Language',
  },
  seo: {
    defaultTitle: 'KUB AUTO — Premium vehicle pre-order across Europe',
    defaultDescription: 'Selection, verification, purchase and delivery of premium vehicles across Europe with fixed terms and legal security.',
    home: {
      title: 'KUB AUTO — Premium vehicle pre-order across Europe',
      description: 'Selection, verification, purchase and delivery of premium vehicles across Europe with fixed terms and legal security.',
    },
    about: {
      title: 'About KUB AUTO — premium vehicle acquisition service',
      description: 'Learn how KUB AUTO organizes premium vehicle sourcing, verification, contracting, logistics and handover across Europe.',
    },
    preOrder: {
      title: 'Pre-Order form — submit vehicle requirements | KUB AUTO',
      description: 'Submit your premium vehicle requirements and receive verified options, clear terms and a contract-based process.',
    },
    contacts: {
      title: 'Contacts — KUB AUTO concierge',
      description: 'Contact KUB AUTO via WhatsApp, Telegram or email for premium vehicle consultations and pre-order requests.',
    },
  },
  home: {
    gallery: {
      kicker: 'SELECTED VEHICLES',
      title: 'Real premium cars from recent sourcing',
      sub: 'A selection of real vehicles we have recently sourced and delivered. Every car reflects our standards in quality, verification, and client-focused service.',
    },
    hero: {
      kicker: 'PRIVATE ACQUISITION SERVICE',
      title: 'Premium vehicles by individual pre-order',
      lead: 'Selection, verification, purchase and delivery across Europe — with fixed terms and legal safety.',
      cta: 'Start Pre-Order',
      pills: ['Contract-based', 'Official databases', 'End-to-end support'],
      cardTitle: 'What you get',
      badge: 'STANDARD',
      bullets: [
        'Documented process and fixed fees',
        'Technical + legal checks before purchase',
        'Logistics, customs, certification, handover',
      ],
    },
    process: {
      title: 'The process',
      sub: 'Clear, documented, predictable.',
      steps: [
        { title: 'Request', sub: 'Requirements' },
        { title: 'Market scan', sub: 'Verified sources' },
        { title: 'Verification', sub: 'Legal + technical' },
        { title: 'Contract', sub: 'Fixed terms' },
        { title: 'Delivery', sub: 'Handover' },
      ],
      ctaTitle: 'Submit requirements — we return with verified options',
      ctaText: 'Typical selection time: a few days up to two weeks.',
      cta: 'Start Pre-Order',
    },
    trust: {
      title: 'Why clients trust us',
      sub: 'Transparency + legal safety across the entire transaction.',
      cards: [
        { title: 'Formal contract', text: 'Fees, stages and timelines fixed.' },
        { title: 'Verification', text: 'Official databases + reports.' },
        { title: 'End-to-end', text: 'Logistics, customs, certification.' },
      ],
      missionTitle: 'Regulated procedure',
      missionText: 'Selection → verification → agreement → contract → purchase → delivery → handover. Every step documented.',
      cta: 'Get a consultation',
    },
    reviewsFaq: {
      reviewsTitle: 'Client notes',
      reviewsSub: 'Short feedback from clients who value predictability, clarity and control.',
      reviews: [
        {
          quote: '“Everything matched what we agreed.”',
          text: 'Clear steps, regular updates, and no pressure. The price and timeline stayed the same as discussed, and the handover was smooth.',
          meta: 'Andrius Kazlauskas, Vilnius (LT)',
        },
        {
          quote: '“I knew what was happening at every stage.”',
          text: 'They sent options with full details, explained what to watch for, and handled the paperwork properly. No unnecessary calls, just clear communication.',
          meta: 'Nerijus Šimkus, Kaunas (LT)',
        },
        {
          quote: '“The car arrived exactly as described.”',
          text: 'Inspection results were shared upfront, logistics were organized without delays, and the documents were ready on time. Overall — professional and calm process.',
          meta: 'Mārtiņš Pētersons, Riga (LV)',
        },
      ],
      faqTitle: 'FAQ',
      faqSub: 'Answers to the most common questions about the process.',
      faq: [
        { q: 'How do I start a pre-order?', a: 'Submit a pre-order form with your requirements. We confirm details and start the selection process.' },
        { q: 'How long does vehicle selection take?', a: 'Typically from a few days up to two weeks, depending on the complexity of requirements.' },
        { q: 'How are vehicles verified?', a: 'Each vehicle undergoes technical and legal verification using official databases and inspection reports.' },
        { q: 'When is the contract signed?', a: 'After you approve a specific vehicle and all conditions are agreed.' },
        { q: 'Which costs are fixed in advance?', a: 'Service fee, logistics, customs clearance and mandatory charges are fixed in the contract.' },
        { q: 'What happens after the vehicle is purchased?', a: 'We organize logistics, customs clearance, certification and preparation for handover.' },
        { q: 'When do I receive the vehicle?', a: 'After all procedures are completed and documents are verified, the vehicle is handed over to you.' },
        { q: 'Can I refuse a selected vehicle?', a: 'Yes. Selection continues until a vehicle fully matching your requirements is approved.' },
        { q: 'Do you work with companies as well as private clients?', a: 'Yes. We work with both individuals and legal entities under a formal contract.' },
        { q: 'In which countries do you source vehicles?', a: 'We source vehicles across Europe, working only with verified suppliers and auction platforms.' },
        { q: 'Is delivery and customs clearance included?', a: 'Yes. Full logistics, customs clearance and certification are part of our service.' },
      ],
    },
    finalCta: {
      kicker: 'READY TO BEGIN',
      title: 'Start your pre-order request',
      sub: 'We’ll respond with verified options and clear terms.',
      cta: 'Proceed to Pre-Order',
    },
  },
  about: {
    hero: {
      kicker: 'ABOUT THE COMPANY',
      title: 'Premium vehicles acquired through individual pre-order across Europe',
      lead: 'We provide professional selection, verification and acquisition of premium vehicles based on individual client requests, with full legal and logistical support from initial search to final handover.',
      cta: 'Submit a pre-order request',
    },
    overview: {
      title: 'General overview',
      sub: 'Our services are designed for clients who value transparency, structured processes and legal security.',
      cards: [
        {
          title: 'Scope of activity',
          text: 'The company specializes in sourcing, verifying and purchasing premium vehicles on the European market, including official dealerships, verified suppliers and auction platforms. All stages — from initial selection to vehicle handover — are performed in accordance with a unified quality standard and agreed timelines.',
        },
        {
          title: 'End-to-end support',
          text: 'We provide vehicle selection based on defined parameters, documented technical and legal verification, fixation of scope and pricing within a contract, organization of purchase, logistics, customs clearance, certification and preparation of the vehicle for final delivery.',
        },
      ],
    },
    principles: {
      title: 'Principles of work',
      sub: 'A structured, documented process with clear responsibility at every stage.',
      cards: [
        { icon: '✓', title: 'Formality and transparency', text: 'All stages of cooperation and service fees are fixed in a contract. The client has full clarity on the process, timelines and obligations of each party.' },
        { icon: '🧾', title: 'Comprehensive vehicle verification', text: 'Each vehicle undergoes technical and legal verification using official databases, service history and, where required, independent expert inspection.' },
        { icon: '🎯', title: 'Individual approach', text: 'Vehicle selection is carried out strictly in accordance with the client’s budget, preferences and intended usage — without random or unsuitable offers.' },
        { icon: '🛡', title: 'Full support', text: 'We organize the purchase, logistics, customs clearance, certification and preparation of the vehicle for handover, minimizing risks and client involvement.' },
      ],
    },
    advantages: {
      title: 'Key advantages',
      sub: 'Why clients choose an individual pre-order format with professional support.',
      items: [
        'Proven expertise in the premium automotive segment',
        'Cooperation exclusively with verified suppliers and auction platforms',
        'Clearly structured workflow and strict adherence to agreed timelines',
        'Responsibility for results at every stage of the transaction',
      ],
    },
    mission: {
      kicker: 'MISSION',
      title: 'To provide access to premium vehicles with guaranteed quality and legal security.',
      text: 'We deliver a structured, transparent and professional service at every stage — from initial selection to final vehicle handover with complete documentation.',
      ctaPrimary: 'Submit a pre-order request',
      ctaSecondary: 'Contact us',
    },
    process: {
      title: 'How the pre-order process works',
      sub: 'A sequential procedure with defined stages and control points.',
      steps: [
        { num: '01', title: 'Request', text: 'You submit vehicle parameters and individual requirements.' },
        { num: '02', title: 'Selection', text: 'We source and present verified vehicle options matching your criteria.' },
        { num: '03', title: 'Contract', text: 'All terms, stages and service fees are fixed contractually.' },
        { num: '04', title: 'Purchase & logistics', text: 'We organize the purchase, delivery, customs clearance and certification.' },
        { num: '05', title: 'Handover', text: 'The vehicle is prepared and handed over with a complete set of documents.' },
      ],
      ctaPrimary: 'Submit a pre-order request',
      ctaSecondary: 'Ask a question on Telegram',
    },
  },
  contacts: {
    hero: {
      kicker: 'CONTACTS',
      title: 'Get in touch',
      lead: 'Fastest way: WhatsApp. We’re available 24/7 for consultation and pre-order requests.',
    },
    primary: {
      title: 'KUB AUTO Concierge',
      emailUs: 'Email us',
      telegramCta: 'Prefer Telegram? Open chat →',
    },
    office: {
      label: 'Office address',
      address: 'Dzūkų g. 6a, Vilnius, 02163 Vilniaus m. sav.',
      badge: '24/7',
      hoursLabel: 'Working hours',
      hoursValue: '24/7',
      supportLabel: 'Support',
      supportValue: 'WhatsApp / Email',
      preOrderLabel: 'Pre-order',
      preOrderValue: 'Contract-based process',
      openMaps: 'Open in Google Maps',
      mapTitle: 'KUB AUTO office map',
    },
    form: {
      title: 'Quick message',
      sub: 'Leave a short note — we’ll reply as soon as possible.',
      nameLabel: 'Your name',
      namePlaceholder: 'Full name',
      emailLabel: 'Email',
      emailPlaceholder: 'name@example.com',
      messageLabel: 'Message',
      messagePlaceholder: 'What car are you looking for? Budget, year, mileage, preferences...',
      submit: 'Send message',
      success: 'Message sent successfully. We’ll get back to you soon.',
      error: 'Failed to send message.',
    },
  },
  preOrder: {
    hero: {
      kicker: 'PRE-ORDER FORM',
      title: 'Submit vehicle requirements',
      lead: 'We’ll review your request and respond with verified options and clear terms.',
    },
    vehicle: {
      title: 'Vehicle details',
      sub: 'Core parameters to start selection.',
      make: 'Make',
      makePlaceholder: 'e.g. BMW',
      model: 'Model',
      modelPlaceholder: 'e.g. X5',
      year: 'Year',
      yearPlaceholder: 'e.g. 2020',
      mileage: 'Mileage (km)',
      mileagePlaceholder: 'e.g. 45000',
      fuelType: 'Fuel type',
      bodyType: 'Body type',
      preferences: 'Additional preferences',
      preferencesPlaceholder: 'Trim, color, interior, condition, usage history, special requirements...',
      fuelOptions: [
        { value: 'petrol', label: 'Petrol' },
        { value: 'diesel', label: 'Diesel' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'electric', label: 'Electric' },
      ],
      bodyOptions: [
        { value: 'sedan', label: 'Sedan' },
        { value: 'wagon', label: 'Wagon' },
        { value: 'coupe', label: 'Coupe' },
        { value: 'suv', label: 'SUV' },
        { value: 'hatchback', label: 'Hatchback' },
        { value: 'other', label: 'Other' },
      ],
    },
    buyer: {
      title: 'Buyer information',
      sub: 'For contract and communication.',
      buyerType: 'Buyer type',
      buyerTypeOptions: [
        { value: 'individual', label: 'Individual' },
        { value: 'company', label: 'Company' },
      ],
      buyerName: 'Name / Company',
      buyerNamePlaceholder: 'Full name or legal entity',
      address: 'Address',
      addressPlaceholder: 'Street, city, country',
      phone: 'Phone',
      phonePlaceholder: '+370 688 03122',
      email: 'Email',
      emailPlaceholder: 'name@example.com',
      submit: 'Submit request',
      askWhatsapp: 'Ask on WhatsApp',
      askTelegram: 'Ask on Telegram',
      success: 'Request sent successfully. We’ll contact you shortly.',
      error: 'Failed to send request.',
    },
    validation: {
      required: 'Required',
      yearFormat: 'Use 4 digits (e.g. 2020)',
      numbersOnly: 'Numbers only',
      tooLong: 'Too long',
      invalidPhone: 'Use international format, e.g. +37068803122',
      invalidEmail: 'Invalid email',
      invalid: 'Invalid',
    },
  },
  validation: {
    tooShort: 'Too short',
    tooLong: 'Too long',
    required: 'Required',
    invalidEmail: 'Invalid email',
    invalid: 'Invalid',
    unknownError: 'Unknown error',
  },
};

export default en;
